/**
 * App-level patterns every module page must use, so all modules look and behave the same:
 * PageHeader, EmptyState, ErrorState, LoadingState, WidgetFrame.
 */
import { AlertTriangle, Inbox, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { Button } from './button.js';
import { Skeleton } from './primitives.js';

export function PageHeader({
  title,
  description,
  actions,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col gap-3 pb-5 sm:flex-row sm:items-end sm:justify-between',
        className,
      )}
    >
      <div className="min-w-0 space-y-1">
        <h1 className="truncate text-xl font-semibold tracking-tight">{title}</h1>
        {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export function EmptyState({
  icon: Icon = Inbox,
  title,
  description,
  action,
  className,
}: {
  icon?: LucideIcon;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed px-6 py-12 text-center',
        className,
      )}
    >
      <div className="flex size-10 items-center justify-center rounded-full bg-muted">
        <Icon className="size-5 text-muted-foreground" />
      </div>
      <div className="max-w-sm space-y-1">
        <p className="font-medium">{title}</p>
        {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
      </div>
      {action}
    </div>
  );
}

export function ErrorState({
  title,
  description,
  retryLabel,
  onRetry,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  retryLabel?: string;
  onRetry?: () => void;
  className?: string;
}) {
  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-lg border border-destructive/25 bg-destructive/5 px-6 py-10 text-center',
        className,
      )}
    >
      <AlertTriangle className="size-6 text-destructive" />
      <div className="max-w-md space-y-1">
        <p className="font-medium">{title}</p>
        {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
      </div>
      {onRetry ? (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          {retryLabel ?? 'Retry'}
        </Button>
      ) : null}
    </div>
  );
}

/** Skeleton placeholder for list/table pages while data loads. */
export function LoadingState({ rows = 5, className }: { rows?: number; className?: string }) {
  return (
    <div className={cn('space-y-2', className)} aria-busy="true">
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="h-10 w-full" style={{ opacity: 1 - i * (0.6 / rows) }} />
      ))}
    </div>
  );
}

/**
 * Frame of a dashboard widget: title, optional action (e.g. a link to the full page), and the
 * loading / error / empty states, so every module's card looks the same.
 */
export function WidgetFrame({
  title,
  action,
  loading,
  error,
  empty,
  children,
  className,
}: {
  title: ReactNode;
  action?: ReactNode;
  loading?: boolean;
  /** Message shown instead of the content. */
  error?: ReactNode;
  /** Message shown when there is nothing to report. */
  empty?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cn(
        'flex h-full min-h-40 flex-col rounded-lg border bg-card p-4 text-card-foreground shadow-xs',
        className,
      )}
      aria-busy={loading || undefined}
    >
      <header className="mb-3 flex items-start justify-between gap-3">
        <h2 className="min-w-0 text-sm leading-5 font-semibold">{title}</h2>
        {action ? (
          <div className="shrink-0 text-xs leading-5 whitespace-nowrap">{action}</div>
        ) : null}
      </header>
      <div className="flex flex-1 flex-col">
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-7 w-1/2" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        ) : error ? (
          <p className="flex items-center gap-2 text-sm text-destructive">
            <AlertTriangle className="size-4 shrink-0" />
            {error}
          </p>
        ) : empty ? (
          <p className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
            {empty}
          </p>
        ) : (
          children
        )}
      </div>
    </section>
  );
}
